import { BsdsInput } from '@bsds-lib-component/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { BsdsInputEvent } from '../../types/componentType';
import type { EmailInputProps } from '../../types/formType';
import { isPersonalEmailDomain } from '../../utils/emailCompanyRule';
import { validateEmail } from '../../utils/emailUtils';



export const EmailInput: React.FC<EmailInputProps> = ({
  value,
  onChange,
  onValidationChange,
  placeholder = "exemplo@email.com",
  required = false,
  disabled = false,
  className = "",
  label,
  company = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  'aria-disabled': ariaDisabled,
  'aria-autocomplete': ariaAutocomplete,
  'aria-haspopup': ariaHasPopup,
  'aria-controls': ariaControls,
  'aria-owns': ariaOwns,
  'aria-activedescendant': ariaActiveDescendant,
  'aria-errormessage': ariaErrorMessage,
}) => {
  const [isValid, setIsValid] = useState<boolean>(true);
  const [hasBeenTouched, setHasBeenTouched] = useState<boolean>(false);
  const inputRef = useRef<HTMLBsdsInputElement>(null);

  // Determine error message
  const getErrorMessage = () => {
    // Ensure value is a string before processing (same as PhoneInput)
    const stringValue = typeof value === 'string' ? value : String(value || '');
    const emailValue = stringValue.trim();

    if (hasBeenTouched && required && emailValue.length === 0) {
      return 'E-mail é obrigatório';
    }

    if (hasBeenTouched && emailValue.length > 0 && !isValid) {
      if (company) {
        return 'Utilize um e-mail corporativo; domínios de e-mail pessoal não são permitidos.';
      }
      return 'E-mail inválido';
    }

    return '';
  };

  const errorMessage = getErrorMessage();

  // Function to update validation classes on the BsdsInput element
  const updateValidationClasses = useCallback(() => {
    if (inputRef.current) {
      const element = inputRef.current;

      // Add bsds-touched class when user has interacted
      if (hasBeenTouched) {
        element.classList.add('bsds-touched');
      } else {
        element.classList.remove('bsds-touched');
      }

      // Add bsds-invalid class when there's an error
      if (hasBeenTouched && !isValid && errorMessage) {
        element.classList.add('bsds-invalid');
      } else {
        element.classList.remove('bsds-invalid');
      }
    }
  }, [hasBeenTouched, isValid, errorMessage]);

  // Handle input change (onChange event - blur)
  const handleChange = useCallback((event: React.FormEvent<HTMLBsdsInputElement>) => {
    const target = event.target as HTMLBsdsInputElement;
    let newValue = (event as BsdsInputEvent).detail?.value || target?.value;

    // If newValue is still undefined/null, try to get from event target directly
    if (newValue === undefined || newValue === null) {
      newValue = '';
    }

    // Ensure newValue is a string before processing (prevent [object Object])
    const stringValue = typeof newValue === 'string' ? newValue : (newValue && typeof newValue === 'object' ? '' : String(newValue || ''));
    onChange(stringValue);
    setHasBeenTouched(true);
  }, [onChange]);

  // Handle input change (onInput event - real time)
  const handleInput = useCallback((event: React.FormEvent<HTMLBsdsInputElement>) => {
    const target = event.target as HTMLBsdsInputElement;
    let newValue = (event as BsdsInputEvent).detail?.value || target?.value;

    // If newValue is still undefined/null, try to get from event target directly
    if (newValue === undefined || newValue === null) {
      newValue = '';
    }

    // Ensure newValue is a string before processing (prevent [object Object])
    const stringValue = typeof newValue === 'string' ? newValue : (newValue && typeof newValue === 'object' ? '' : String(newValue || ''));
    onChange(stringValue);
    setHasBeenTouched(true);
  }, [onChange]);

  // Validate email when value changes
  useEffect(() => {
    // Ensure value is a string before processing (same as PhoneInput)
    const stringValue = typeof value === 'string' ? value : String(value || '');
    const emailValue = stringValue.trim();

    if (emailValue.length === 0) {
      // Empty value - valid if not required, invalid if required
      const valid = !required;
      setIsValid(valid);
      onValidationChange?.(valid);
    } else {
      // First validate email format
      const isValidFormat = validateEmail(emailValue);

      if (!isValidFormat) {
        setIsValid(false);
        onValidationChange?.(false);
        return;
      }

      // Modo corporativo: domínio não pode estar na lista de e-mails pessoais/gratuitos
      if (company) {
        const isValidDomain = !isPersonalEmailDomain(emailValue);
        setIsValid(isValidDomain);
        onValidationChange?.(isValidDomain);
      } else {
        // Regular email validation (format only)
        setIsValid(isValidFormat);
        onValidationChange?.(isValidFormat);
      }
    }
  }, [value, onValidationChange, required, company]);

  // Update validation classes when state changes
  useEffect(() => {
    updateValidationClasses();
  }, [updateValidationClasses]);

  // Ensure value is always a string for the input (prevent [object Object])
  const safeValue = typeof value === 'string' ? value : String(value || '');

  return (
    <BsdsInput
      ref={inputRef}
      value={safeValue}
      label={label}
      onChange={handleChange}
      onInput={handleInput}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      className={className}
      type="email"
      inputMode="email"
      errorText={errorMessage}
      hasHintIcon
      color="primary"
      clearInput={false}
      platform="web"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-required={ariaRequired}
      aria-disabled={ariaDisabled}
      aria-autocomplete={ariaAutocomplete}
      aria-haspopup={ariaHasPopup}
      aria-controls={ariaControls}
      aria-owns={ariaOwns}
      aria-activedescendant={ariaActiveDescendant}
      aria-errormessage={ariaErrorMessage}
    />
  );
};

